import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LifeBuoy, RefreshCw, Search } from 'lucide-react';

import { PageHeader } from '@/components/page-header';
import { ErrorNotice } from '@/components/ui/error-notice';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UserDetailsDialog } from '@/components/admin/UserDetailsDialog';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { formatUnixDateTimeCompact } from '@/lib';
import {
  SupportTicketClosedReason,
  SupportTicketContextType,
  type ISupportTicket,
  type SupportTicketStatus,
} from '@/domain/entities/support';
import {
  SUPPORT_QUEUE_STATUSES,
  fetchSupportTicketsByStatus,
  selectActiveSupportQueueStatus,
  selectSupportTicketsByStatus,
  selectSupportTicketsErrorByStatus,
  selectSupportTicketsHasFetchedByStatus,
  selectSupportTicketsLoadingByStatus,
  selectSupportTicketsNextTokenByStatus,
  setActiveSupportQueueStatus,
} from '@/features/support';

const QUEUE_LABELS: Record<SupportTicketStatus, string> = {
  open: 'Open',
  closed: 'Closed',
};

const QUEUE_BADGE_VARIANTS: Record<SupportTicketStatus, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  open: 'outline',
  closed: 'outline',
};

const PRIORITY_BADGE_VARIANTS: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  low: 'outline',
  normal: 'secondary',
  high: 'destructive',
};

function getRequesterLabel(ticket: ISupportTicket) {
  const fullName = [ticket.openedBySnapshot.firstName, ticket.openedBySnapshot.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  return fullName || ticket.openedBySnapshot.email || ticket.openedByUserID;
}

function getWorkflowLabel(ticket: ISupportTicket): string {
  if (ticket.status === 'open') {
    return ticket.awaiting === 'user' ? 'Waiting On User' : 'Waiting On Support';
  }

  switch (ticket.closedReason) {
    case SupportTicketClosedReason.Resolved:
      return 'Resolved';
    case SupportTicketClosedReason.ClosedByHomeowner:
      return 'Closed by Homeowner';
    case SupportTicketClosedReason.ClosedBySupport:
      return 'Closed by Support';
    default:
      return 'Closed';
  }
}

function getWorkflowBadgeVariant(ticket: ISupportTicket): 'default' | 'secondary' | 'outline' | 'destructive' {
  if (ticket.status === 'open') {
    return ticket.awaiting === 'user' ? 'secondary' : 'destructive';
  }

  return ticket.closedReason === SupportTicketClosedReason.Resolved ? 'default' : 'outline';
}

function getRecoveryCaseId(ticket: ISupportTicket): string | undefined {
  return ticket.contextType === SupportTicketContextType.IdentityRecovery ? ticket.contextID || undefined : undefined;
}

export default function SupportTicketsPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const activeStatus = useAppSelector(selectActiveSupportQueueStatus);
  const tickets = useAppSelector(selectSupportTicketsByStatus(activeStatus));
  const isLoading = useAppSelector(selectSupportTicketsLoadingByStatus(activeStatus));
  const error = useAppSelector(selectSupportTicketsErrorByStatus(activeStatus));
  const hasFetched = useAppSelector(selectSupportTicketsHasFetchedByStatus(activeStatus));
  const nextToken = useAppSelector(selectSupportTicketsNextTokenByStatus(activeStatus));

  const [query, setQuery] = useState('');

  useEffect(() => {
    if (hasFetched || isLoading) return;
    void dispatch(fetchSupportTicketsByStatus({ status: activeStatus }));
  }, [activeStatus, dispatch, hasFetched, isLoading]);

  const filteredTickets = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tickets;

    return tickets.filter((ticket) => {
      const requester = getRequesterLabel(ticket).toLowerCase();
      return (
        ticket.id.toLowerCase().includes(q) ||
        ticket.title.toLowerCase().includes(q) ||
        (ticket.latestMessagePreview ?? '').toLowerCase().includes(q) ||
        requester.includes(q) ||
        (ticket.propertyID ?? '').toLowerCase().includes(q) ||
        (ticket.contextID ?? '').toLowerCase().includes(q)
      );
    });
  }, [query, tickets]);

  const refresh = () => {
    if (isLoading) return;
    void dispatch(fetchSupportTicketsByStatus({ status: activeStatus }));
  };

  const loadMore = () => {
    if (!nextToken || isLoading) return;
    void dispatch(fetchSupportTicketsByStatus({ status: activeStatus, nextToken }));
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Support Tickets"
        description="Super-admin queue for homeowner and business support requests."
        right={
          <Button type="button" variant="outline" onClick={refresh} disabled={isLoading}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        }
      />

      <Tabs
        value={activeStatus}
        onValueChange={(value) => dispatch(setActiveSupportQueueStatus(value as SupportTicketStatus))}
      >
        <TabsList className="w-full justify-start overflow-x-auto">
          {SUPPORT_QUEUE_STATUSES.map((status) => (
            <TabsTrigger key={status} value={status} className="px-3">
              {QUEUE_LABELS[status]}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="flex flex-col gap-4 md:flex-row md:items-center">
        <div className="relative flex-1">
          <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by title, requester, property id, preview, or ticket id…"
            className="pl-9"
          />
        </div>
      </div>

      <ErrorNotice message={error} title="Failed to load support tickets" />

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Requester</TableHead>
              <TableHead>Property</TableHead>
              <TableHead>Queue</TableHead>
              <TableHead>Workflow</TableHead>
              <TableHead>Priority</TableHead>
              <TableHead>Last Message</TableHead>
              <TableHead>Updated</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && !hasFetched ? (
              <TableRow>
                <TableCell colSpan={8} className="h-24">
                  <div className="flex justify-center">
                    <LoadingSpinner />
                  </div>
                </TableCell>
              </TableRow>
            ) : filteredTickets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="h-28 text-center">
                  <div className="text-muted-foreground flex flex-col items-center gap-2">
                    <LifeBuoy className="h-5 w-5" />
                    <span>No tickets found for {QUEUE_LABELS[activeStatus].toLowerCase()}.</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              filteredTickets.map((ticket) => {
                const recoveryCaseId = getRecoveryCaseId(ticket);

                return (
                  <TableRow
                    key={ticket.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/admin/support/${encodeURIComponent(ticket.id)}`)}
                  >
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <span className="font-medium">{ticket.title}</span>
                        <span className="text-muted-foreground text-xs">{ticket.id}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <UserDetailsDialog
                        userId={ticket.openedByUserID}
                        trigger={
                          <button
                            type="button"
                            className="hover:text-foreground text-left underline-offset-4 hover:underline"
                            onClick={(event) => event.stopPropagation()}
                          >
                            {getRequesterLabel(ticket)}
                          </button>
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {ticket.propertyID ? (
                        <Link
                          to={`/properties/${encodeURIComponent(ticket.propertyID)}/edit`}
                          className="hover:underline"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {ticket.propertyID}
                        </Link>
                      ) : recoveryCaseId ? (
                        <Link
                          to={`/admin/recovery-cases/${encodeURIComponent(recoveryCaseId)}`}
                          className="hover:underline"
                          onClick={(event) => event.stopPropagation()}
                        >
                          Recovery case
                        </Link>
                      ) : (
                        'N/A'
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={QUEUE_BADGE_VARIANTS[ticket.status]}>{QUEUE_LABELS[ticket.status]}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getWorkflowBadgeVariant(ticket)}>{getWorkflowLabel(ticket)}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={PRIORITY_BADGE_VARIANTS[ticket.priority] ?? 'secondary'}>{ticket.priority}</Badge>
                    </TableCell>
                    <TableCell className="max-w-[320px] truncate text-sm text-slate-600">
                      {ticket.latestMessagePreview || 'No messages'}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatUnixDateTimeCompact(ticket.updatedAt)}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {nextToken ? (
        <div className="flex justify-center">
          <Button type="button" variant="outline" onClick={loadMore} disabled={isLoading}>
            Load More
          </Button>
        </div>
      ) : null}
    </div>
  );
}
